import styles from './PricingTableCellContent.scss';

import cx from 'classnames';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { BundleFeatureValueVariant } from 'src/api/contentful/contentTypes.cloverdotcom';
import { ContentImage } from 'src/components/ContentImage/ContentImage';
import { ContentLinkV2 } from 'src/components/ContentLinkV2/ContentLinkV2';
import { TextV2 } from 'src/components/TextV2/TextV2';
import { BundleFeatureValue } from 'src/services/cms/bundleTypes';
import { TransactionRate } from 'src/services/dcs/bundle';
import { ProductCellContent } from '../ProductCellContent/ProductCellContent';

export const KEYED_IN_RATE_KEY = '{{KEYED_IN_TRANSACTIONS}}';
export const IN_PERSON_RATE_KEY = '{{IN_PERSON_TRANSACTIONS}}';

interface PricingTableCellContentProps {
  value?: BundleFeatureValue;
  keyedInRate?: TransactionRate;
  inPersonRate?: TransactionRate;
}

/**
 * Simple re-usable method to output a common UI for items not included
 */
export const FeatureNotIncluded = () => {
  const { t } = useTranslation();
  return (
    <div className={cx(styles.featureInclusion, styles.featureInclusionNotIncluded)}>
      <span className={styles.featureInclusionStatus}>{t('pricing-detail-table.not-included')}</span>
    </div>
  );
};
export const FeatureIncluded = () => {
  const { t } = useTranslation();
  return (
    <div className={cx(styles.featureInclusion, styles.featureInclusionIncluded)}>
      <span className={styles.featureInclusionStatus}>{t('pricing-detail-table.included')}</span>
    </div>
  );
};

function renderCellBody(value: BundleFeatureValue = {}): React.ReactNode {
  const { variant, image, included } = value;

  const { Included, Image: ImageVariant } = BundleFeatureValueVariant;

  if (variant === Included && included) {
    return <FeatureIncluded />;
  }

  if (variant === Included && !included) {
    return <FeatureNotIncluded />;
  }

  if (variant === ImageVariant) {
    return <ContentImage entry={image} className={styles.cellImage} />;
  }

  return null;
}

function renderPricing({ text, keyedInRate, inPersonRate }: { text?: string; keyedInRate?: TransactionRate; inPersonRate?: TransactionRate }) {
  const { t } = useTranslation();

  switch (text) {
    case KEYED_IN_RATE_KEY:
      return keyedInRate ? t('pricing-detail-table.transaction-rate', { percentage: keyedInRate.percentage, fixedAmount: keyedInRate.fixedAmount.value }) : '';
    case IN_PERSON_RATE_KEY:
      return inPersonRate ? t('pricing-detail-table.transaction-rate', { percentage: inPersonRate.percentage, fixedAmount: inPersonRate.fixedAmount.value }) : '';
    default:
      return text || '-';
  }
}

export const PricingTableCellContent: React.FC<PricingTableCellContentProps> = ({ value, keyedInRate, inPersonRate }) => {
  if (!value) {
    return <FeatureNotIncluded />;
  }

  const { variant, product, text, link } = value;
  if (variant === BundleFeatureValueVariant.Product && product) {
    return <ProductCellContent productDetails={product} />;
  }

  let pricingText = null;
  if (variant === BundleFeatureValueVariant.Pricing) {
    pricingText = renderPricing({ text, keyedInRate, inPersonRate });
  }

  if (variant === BundleFeatureValueVariant.Link) {
    return (
      <ContentLinkV2 entry={link} hideTitle>
        <TextV2
          isMarkdown
          value={pricingText === null ? text : pricingText}
          variant={pricingText === null ? 'body-small' : 'h5-medium'}
          className={pricingText ? styles.cellText : styles.cellRichText}
        />
      </ContentLinkV2>
    );
  }

  return (
    <>
      {renderCellBody(value)}
      <TextV2
        isMarkdown
        value={pricingText === null ? text : pricingText}
        variant={pricingText === null ? 'body-small' : 'h5-medium'}
        className={pricingText === null ? styles.cellRichText : styles.cellText}
      />
    </>
  );
};
